import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Divider,
  CircularProgress,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Toast } from 'primereact/toast';
import CustomAxios from '../../utils/CustomAxios';

const GROWTH_FONT = '"Poppins", "Segoe UI", sans-serif';
const PINK = '#ED1E79';
const GREEN = '#1f8a4c';
const GREY = '#9aa0ac';

// Friendly labels for the feature keys the backend exposes.
const FEATURE_LABELS = {
  promo_coupon: { title: 'Promo coupons', sub: 'New / updated coupon codes' },
  offers: { title: 'Offers & smart deals', sub: 'Offers and subscriber smart-deal pushes' },
  loyalty: { title: 'Loyalty & wallet bonus', sub: 'Earn-rate and top-up bonus changes' },
  referral: { title: 'Referral rewards', sub: 'Program changes and reward earned' },
  subscription: { title: 'Subscription plans & reminders', sub: 'Plan changes and expiry reminders' },
};

/**
 * Growth Hub master + per-feature PUSH notification switches.
 * Collapsible: the header (master switch + summary) is always visible; the
 * per-feature list expands on click. Reads/writes /growth-notification-config/.
 * Gates device push only — the in-app bell and order/payment pushes are never
 * affected.
 */
export default function PushNotificationPanel() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(true);
  const [flags, setFlags] = useState({});
  const [features, setFeatures] = useState([]);

  const showToast = (severity, summary, detail) =>
    toast.current?.show({ severity, summary, detail, life: 2500 });

  useEffect(() => {
    (async () => {
      try {
        const res = await CustomAxios.get('growth-notification-config/');
        setPushEnabled(Boolean(res.data?.push_enabled));
        setFlags(res.data?.feature_flags || {});
        setFeatures(res.data?.features || Object.keys(res.data?.feature_flags || {}));
      } catch (err) {
        showToast('error', 'Error', 'Could not load notification settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onCount = useMemo(
    () => features.filter((k) => flags[k] !== false).length,
    [features, flags]
  );

  const summaryText = !pushEnabled
    ? 'All paused'
    : onCount === features.length
    ? 'All features on'
    : `${onCount} of ${features.length} on`;

  const persist = async (payload, revert) => {
    try {
      setSaving(true);
      const res = await CustomAxios.put('growth-notification-config/', payload);
      setPushEnabled(Boolean(res.data?.push_enabled));
      setFlags(res.data?.feature_flags || {});
    } catch (err) {
      revert?.();
      showToast('error', 'Error', 'Could not save — reverted');
    } finally {
      setSaving(false);
    }
  };

  const toggleMaster = (e) => {
    const next = e.target.checked;
    const prev = pushEnabled;
    setPushEnabled(next);
    persist({ push_enabled: next }, () => setPushEnabled(prev));
  };

  const toggleFeature = (key) => (e) => {
    const next = e.target.checked;
    const prevFlags = flags;
    setFlags({ ...flags, [key]: next });
    persist({ feature_flags: { [key]: next } }, () => setFlags(prevFlags));
  };

  const stop = (e) => e.stopPropagation();

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: '18px',
        backgroundColor: '#fff',
        border: '1px solid #F0E3EC',
        boxShadow: expanded ? '10px 12px 26px 0px #ED1E7918' : '6px 8px 16px 0px #ED1E7912',
        fontFamily: GROWTH_FONT,
        overflow: 'hidden',
        transition: 'box-shadow 200ms ease',
      }}
    >
      <Toast ref={toast} />

      {/* ---- Header (click to expand) ---- */}
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          cursor: 'pointer',
          px: { xs: 2, md: 2.75 },
          py: { xs: 1.75, md: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          background: pushEnabled
            ? 'linear-gradient(120deg, #FFF 60%, #FDEFF6 100%)'
            : 'linear-gradient(120deg, #FFF 60%, #FBF1E7 100%)',
          '&:hover': { backgroundColor: '#FEFAFC' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 42, height: 42, borderRadius: '12px', flex: 'none',
              backgroundColor: pushEnabled ? `${PINK}14` : '#F3E6E6',
              color: pushEnabled ? PINK : '#b23b3b',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {pushEnabled ? <NotificationsActiveIcon fontSize="small" /> : <NotificationsOffIcon fontSize="small" />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 16, fontWeight: 700 }}>
                Growth push notifications
              </Typography>
              <Chip
                size="small"
                label={summaryText}
                sx={{
                  height: 20, fontSize: 11, fontWeight: 600, fontFamily: GROWTH_FONT,
                  backgroundColor: pushEnabled ? '#EAF6EE' : '#FBF1E7',
                  color: pushEnabled ? GREEN : '#8a6d2f',
                }}
              />
            </Box>
            <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 12.5, color: 'text.secondary' }}>
              Off stops these notifications entirely — no phone push and no in-app alert. Order & payment updates are never affected.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }} onClick={stop}>
          {saving ? <CircularProgress size={16} sx={{ mr: 0.5 }} /> : null}
          <Chip
            size="small"
            label={pushEnabled ? 'ON' : 'ALL OFF'}
            sx={{
              fontWeight: 700, fontFamily: GROWTH_FONT,
              backgroundColor: pushEnabled ? '#E2F3E8' : '#F3E6E6',
              color: pushEnabled ? GREEN : '#b23b3b',
            }}
          />
          <Switch checked={pushEnabled} onChange={toggleMaster} disabled={loading || saving} />
          <IconButton
            size="small"
            onClick={(e) => { stop(e); setExpanded((v) => !v); }}
            sx={{
              transition: 'transform 220ms ease',
              transform: expanded ? 'rotate(180deg)' : 'none',
            }}
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            <ExpandMoreRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      {/* ---- Collapsible per-feature list ---- */}
      <Collapse in={expanded} timeout={260} unmountOnExit>
        <Divider />
        <Box sx={{ px: { xs: 2, md: 2.75 }, py: 1.5 }}>
          <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>
            Per-feature
          </Typography>

          {loading ? (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            features.map((key, idx) => {
              const meta = FEATURE_LABELS[key] || { title: key, sub: '' };
              const on = flags[key] !== false; // missing = on
              const active = pushEnabled && on;
              return (
                <Box key={key}>
                  <Box
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 2, py: 1.15, opacity: pushEnabled ? 1 : 0.5,
                      transition: 'opacity 200ms ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 8, height: 8, borderRadius: '50%', flex: 'none',
                          backgroundColor: active ? GREEN : GREY,
                          boxShadow: active ? `0 0 0 3px ${GREEN}22` : 'none',
                          transition: 'background-color 200ms ease, box-shadow 200ms ease',
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 14, fontWeight: 600 }}>
                          {meta.title}
                        </Typography>
                        <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 12, color: 'text.secondary' }}>
                          {meta.sub}
                        </Typography>
                      </Box>
                    </Box>
                    <Switch
                      checked={on}
                      onChange={toggleFeature(key)}
                      disabled={!pushEnabled || saving}
                      size="small"
                    />
                  </Box>
                  {idx < features.length - 1 ? <Divider /> : null}
                </Box>
              );
            })
          )}

          {!loading && !pushEnabled ? (
            <Box sx={{ mt: 1.5, p: 1.25, borderRadius: '10px', backgroundColor: '#FDF4E7', border: '1px solid #F0D9AE' }}>
              <Typography sx={{ fontFamily: GROWTH_FONT, fontSize: 12.5, color: '#8a6d2f' }}>
                All growth notifications are off — no phone push and no in-app alert go out. Order & payment updates are unaffected.
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}
